import React, { useEffect, useMemo, useRef, useState } from "react";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import VarianceValue from "../../../../../common/VarianceValue/VarianceValue";
import styles from "./TotalIncomeTile.module.scss";
import { useTranslation } from "react-i18next";
import Benchmark from "../../../../../common/Benchmark/Benchmark";
import ViewModeSwitcher, { ViewMode } from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import GenericTable from "../../../../../common/GenericTable/GenericTable";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import { isEmpty } from "lodash";
import { useGetAGCAIDetailsMutation } from "../../../../../../redux/services/agcAIAPI";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import { useProfileParams } from "../../../../../../utils/common";
import StackedBarChart from "../../../../../common/StackedBarChart/StackedBarChart";
import { CurrencyInputFormatter } from "../../../../../../utils/currencyFormatter";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import { isValidLineChartData } from "../../../../../../utils/common";
import { sanitizeTableData } from "../../../../../../utils/expandableTableDataSanitizer";
import { stackedBarDataSanitizer } from "../../../../../../utils/stackedBarChartDataSanitizer";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

const TotalIncomeTile: React.FC<SummaryProp> = ({ summary, loading }) => {
  const { t } = useTranslation();
  const valueNum = typeof summary?.kpiValue === "number" ? summary.kpiValue : 0;
  const benchmark = summary?.benchmark;

  return (
    <KPITile
      title={t("totalIncome.title")}
      dataTestId="tile-total-income"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.value_wrapper}>
          <div className={styles.value_content}>
            {CurrencyInputFormatter("USD", valueNum)}
          </div>
          <VarianceValue
            value={summary?.kpiVarianceValue ?? 0}
            dataType={summary?.kpiVarianceDataType}
            comparisonCriteria={summary?.kpiVarianceComparisonCriteria}
          />
        </div>
        <div className={styles.bar_wrapper}>
          <Benchmark
            minValue={benchmark?.min}
            maxValue={benchmark?.max}
            medianValue={benchmark?.median}
            quartileLine={[
              benchmark?.lowerQuartile ?? 0,
              benchmark?.upperQuartile ?? 0,
            ]}
            currentBenchmarkValue={valueNum}
            benchmarkDataType={summary?.benchmarkDataType}
            currency={summary?.kpiCurrency}
            isPercentValue={false}
          />
        </div>
      </div>
    </KPITile>
  );
};

export default TotalIncomeTile;

interface TotalIncomeMonthlyViewProps {
  viewMode: ViewMode;
  lineChartData: any;
  tableData: any;
  currency: string;
  chartSummaryData?: any;
  kpiCode: string;
}

const TotalIncomeMonthlyView: React.FC<TotalIncomeMonthlyViewProps> = ({
  viewMode,
  lineChartData,
  tableData,
  currency,
  chartSummaryData,
  kpiCode,
}) => {
  const { t } = useTranslation();

  if (viewMode === "left") {
    return isEmpty(lineChartData) ? (
      <div className={styles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <GraphCard
        lineChartData={lineChartData}
        currency={currency}
        chartSummaryData={chartSummaryData}
      />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t("noDataAvailable")}</div>
  ) : (
    <GenericTable tableData={tableData} kpiCode={kpiCode} highlightColumns={[]} />
  );
};

interface TotalIncomeYearlyViewProps {
  viewMode: ViewMode;
  tableData: any;
  stackedBarChartData?: any;
  kpiCode: string;
  yAxisValues?: number[];
  currency?: string;
}

const TotalIncomeYearlyView: React.FC<TotalIncomeYearlyViewProps> = ({
  viewMode,
  tableData,
  stackedBarChartData,
  kpiCode,
  yAxisValues,
  currency = "USD",
}) => {
  const { t } = useTranslation();
  if (viewMode === "left") {
    return isEmpty(stackedBarChartData) ? (
      <div className={styles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <StackedBarChart data={stackedBarChartData} currency={currency} yAxisValues={yAxisValues} />
    );
  }

  return isEmpty(tableData) ? (
    <div className={styles.no_data}>{t("noDataAvailable")}</div>
  ) : (
    <GenericTable tableData={tableData} kpiCode={kpiCode} highlightColumns={[1]} />
  );
};

// --- Detail Modal/Expanded View ---
export const TotalIncomeDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("left");
  const [periodCode, setPeriodCode] = useState<string>("monthly");
  const kpiCode = "totalIncome";
  const { affiliateCode, aboNumber } = useProfileParams();

  // 1. API Fetching
  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  // 2. Memoized Data Processing (Prevents "Infinite Re-render" black screen)
  const detailData = useMemo(() => detailsResponse?.body?.kpis?.[0] || {}, [detailsResponse]);
  const currency = detailData?.kpiCurrency || "USD";

  const monthlyData = useMemo(() => {
    const charts = detailData?.detail?.monthlyView?.chart || [];
    const lineChart = charts.find((c: any) => c?.chartType === "lineChart");
    const tableChart = charts.find((c: any) => c?.chartType === "table");
    return {
      lineChartData: lineChart?.chartDetail?.lineChartDetail,
      tableDetail: tableChart?.chartDetail?.tableDetail,
      chartSummary: lineChart?.chartSummary,
    };
  }, [detailData]);

  const yearlyData = useMemo(() => {
    const charts = (detailData?.detail?.annuallyView?.chart || detailData?.detail?.anuallyView?.chart || []);
    const barChart = charts.find((c: any) => c?.chartType === "barChart" || c?.chartType === "stackedBarChart");
    const tableChart = charts.find((c: any) => c?.chartType === "table");
    const rawBarDetail = barChart?.chartDetail?.barChartDetail || barChart?.chartDetail?.stackedBarChartDetail;
    
    return {
      rawBarDetail,
      tableDetail: tableChart?.chartDetail?.tableDetail,
    };
  }, [detailData]);

  const yAxisValues = useMemo(() => {
    if (!yearlyData.rawBarDetail?.yAxis || !Array.isArray(yearlyData.rawBarDetail.yAxis)) return undefined;
    return yearlyData.rawBarDetail.yAxis.map((item: any) => Number(item.dataValue));
  }, [yearlyData.rawBarDetail]);

  const stackedBarChartData = useMemo(
    () => stackedBarDataSanitizer(yearlyData.rawBarDetail),
    [yearlyData.rawBarDetail]
  );

  const tableData = useMemo(() => {
    const raw = periodCode === "monthly" ? monthlyData.tableDetail : yearlyData.tableDetail;
    return sanitizeTableData(raw);
  }, [periodCode, monthlyData.tableDetail, yearlyData.tableDetail]);

  const hasValidLineChart = useMemo(() => isValidLineChartData(monthlyData.lineChartData), [monthlyData.lineChartData]);
  
  const hasChartData = useMemo(() => {
    return periodCode === "monthly" ? hasValidLineChart : !isEmpty(stackedBarChartData);
  }, [periodCode, hasValidLineChart, stackedBarChartData]);

  // 3. Side Effects
  useEffect(() => {
    if (!hasChartData && !isLoading) {
      setViewMode("right");
    }
  }, [hasChartData, isLoading]);

  const aiContent = useMemo(() => {
    const aiRaw = aiDetailsResp?.body;
    const aiResolved = typeof aiRaw === "string" ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || "";
    if (!aiResolved) return "";
    const baseAI = String(aiResolved).slice(0, 300).concat("...");
    return `${periodCode === "monthly" ? "[Monthly] " : "[Yearly] "}${baseAI}`;
  }, [aiDetailsResp, periodCode, kpiCode]);

  const periodItems = useMemo(() => [
    { code: "monthly", label: t("monthly_view") },
    { code: "yearly", label: t("yearly_view") },
  ], [t]);

  if (isLoading) return <Skeleton height="400px" />;

  // 4. Render Logic
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t("totalIncome.title")}</div>
        <div className={styles.desc}>{t("totalIncome.description")}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        timeRanges={periodItems}
        selectedTimeRangeCode={periodCode}
        onSelectTimeRange={setPeriodCode}
        showSwitch={hasChartData}
      />
    </div>
  );

  const leftContent = periodCode === "monthly" ? (
    <TotalIncomeMonthlyView
      viewMode={viewMode}
      lineChartData={hasValidLineChart ? monthlyData.lineChartData : undefined}
      tableData={tableData}
      currency={currency}
      chartSummaryData={monthlyData.chartSummary}
      kpiCode={kpiCode}
    />
  ) : (
    <TotalIncomeYearlyView
      viewMode={viewMode}
      tableData={tableData}
      stackedBarChartData={stackedBarChartData}
      kpiCode={kpiCode}
      yAxisValues={yAxisValues}
      currency={currency}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={
        isLoadingAiSummary ? (
          <Skeleton height="200px" />
        ) : (
          <InsightAI
            updateDate=""
            content={aiContent}
            title={t("ai_summary")}
            showDropdown={false}
            isDropdownEnabled={true}
            flowerIconSize={20}
            infoIconSize={12}
            titleSize={14}
          />
        )
      }
      onClose={onClose!}
      dataTestId="total-income-detail"
    />
  );
};